import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '../mock';
import ProductCard from '../components/ProductCard';
import { SlidersHorizontal, X } from 'lucide-react';
import api from '../api';

const sortOptions = [
  { id: 'featured', label: 'Featured' },
  { id: 'price-asc', label: 'Price, low to high' },
  { id: 'price-desc', label: 'Price, high to low' },
  { id: 'new', label: 'Newest' }
];

export default function Shop() {
  const { category } = useParams();
  const [sort, setSort] = useState('featured');
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceMax, setPriceMax] = useState(20000);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeCat = categories.find(c => c.id === category);

  useEffect(() => {
    setLoading(true);
    const url = category ? `/products?category=${category}` : '/products';
    api.get(url).then(r => setProducts(r.data)).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [category]);

  const filtered = useMemo(() => {
    let list = products.filter(p => p.price <= priceMax);
    if (sort === 'price-asc') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') list = [...list].sort((a, b) => b.price - a.price);
    if (sort === 'new') list = [...list].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    return list;
  }, [products, sort, priceMax]);

  return (
    <main>
      {activeCat ? (
        <section data-testid="category-banner" className="relative overflow-hidden border-b border-black/10">
          <div className="grid lg:grid-cols-2">
            <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[520px] overflow-hidden bg-[#E9E1D4]">
              <img key={activeCat.id} src={activeCat.image} alt={activeCat.name} className="absolute inset-0 w-full h-full object-cover fade-up" />
            </div>
            <div className="flex flex-col justify-center px-6 py-12 lg:px-16 lg:py-20">
              <nav className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/50 mb-8 fade-up">
                <Link to="/" className="hover:text-[#C6410E]">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/shop" className="hover:text-[#C6410E]">Shop</Link>
                <span className="mx-2">/</span><span className="text-[#1a1a1a]">{activeCat.name}</span>
              </nav>
              <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4 fade-up fade-up-delay-1">{activeCat.count} pieces · {activeCat.subtitle}</p>
              <h1 data-testid="category-title" className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] fade-up fade-up-delay-1">{activeCat.name}</h1>
              <p className="font-serif-display italic text-2xl md:text-3xl text-[#1a1a1a]/80 mt-6 fade-up fade-up-delay-2">{activeCat.tagline}</p>
              <p data-testid="category-intro" className="text-[#1a1a1a]/70 mt-4 max-w-md leading-relaxed fade-up fade-up-delay-3">{activeCat.intro}</p>
            </div>
          </div>
        </section>
      ) : (
        <section className="border-b border-black/10">
          <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
            <nav className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/50 mb-6">
              <Link to="/" className="hover:text-[#C6410E]">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-[#1a1a1a]">Shop</span>
            </nav>
            <h1 className="font-serif-display text-5xl md:text-7xl">The Collection</h1>
            <p className="text-[#1a1a1a]/70 mt-4 max-w-2xl">Every piece in the barebasics collection, arranged for browsing at your own pace.</p>
          </div>
        </section>
      )}

      <section className="border-b border-black/10 sticky top-[124px] lg:top-[164px] bg-[#F4EEE4] z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4 overflow-x-auto no-scrollbar">
          <Link to="/shop" className={`text-[11px] tracking-widest-plus uppercase whitespace-nowrap ${!category ? 'text-[#C6410E]' : ''} link-underline`}>All</Link>
          {categories.map(c => (
            <Link key={c.id} to={`/shop/${c.id}`} className={`text-[11px] tracking-widest-plus uppercase whitespace-nowrap ${c.id === category ? 'text-[#C6410E]' : ''} link-underline`}>{c.name}</Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <button onClick={() => setFilterOpen(v => !v)} className="flex items-center gap-2 text-[11px] tracking-widest-plus uppercase">
          <SlidersHorizontal className="w-4 h-4" /> {filterOpen ? 'Hide filters' : 'Filters'}
        </button>
        <div className="flex items-center gap-3">
          <span className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/50">Sort</span>
          <select value={sort} onChange={e => setSort(e.target.value)} className="bg-transparent border-b border-black/30 pb-1 text-sm outline-none">
            {sortOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid grid-cols-1 md:grid-cols-4 gap-8">
        {filterOpen && (
          <aside className="md:col-span-1 border border-black/10 p-6 h-fit sticky top-[220px]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[11px] tracking-widest-plus uppercase">Filter</h3>
              <button onClick={() => setFilterOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="mb-8">
              <label className="text-[11px] tracking-widest-plus uppercase block mb-3">Max Price</label>
              <input type="range" min="1000" max="25000" step="500" value={priceMax} onChange={e => setPriceMax(+e.target.value)} className="w-full accent-[#C6410E]" />
              <p className="text-sm mt-2">Up to &#8377;{priceMax.toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-[11px] tracking-widest-plus uppercase mb-3">Colour</p>
              <div className="flex gap-2 flex-wrap">
                {['Cognac', 'Ebony', 'Tan', 'Ink', 'Forest', 'Oxblood'].map(c => (
                  <span key={c} className="text-xs border border-black/20 rounded-full px-3 py-1">{c}</span>
                ))}
              </div>
            </div>
          </aside>
        )}
        <div className={`grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 ${filterOpen ? 'md:col-span-3' : 'md:col-span-4 md:grid-cols-4'}`}>
          {loading && <p className="col-span-full text-center py-10 text-sm text-[#1a1a1a]/50">Loading…</p>}
          {!loading && filtered.map(p => <ProductCard key={p.id} product={p} />)}
          {!loading && filtered.length === 0 && (
            <p className="col-span-full text-center py-24 font-serif-display text-2xl text-[#1a1a1a]/50">Nothing to show — try widening your filter.</p>
          )}
        </div>
      </section>
    </main>
  );
}
